/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

/**
 * Button Variants Reference
 *
 * NAMING NOTE: Our variant names follow brand hierarchy, not typical conventions:
 *
 * | Variant     | Visual          | Use Case                              |
 * |-------------|-----------------|---------------------------------------|
 * | default     | Gold (accent)   | Primary CTAs - main action per screen |
 * | neutral     | Black           | Secondary actions, form submits       |
 * | outline     | Border only     | Tertiary actions, cancel buttons      |
 * | ghost       | Transparent     | Navigation, toolbar actions           |
 * | destructive | Red             | Delete, remove actions                |
 * | link        | Text + underline| Inline links                          |
 *
 * The "default" variant is intentionally gold (not neutral) because our brand
 * uses the gold accent color for primary call-to-actions.
 */

const BASE_BUTTON_CLASSES =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:pointer-events-none [&>svg]:shrink-0 [&>svg]:mr-0 active:scale-[0.97] active:brightness-95";

const buttonVariants = cva(BASE_BUTTON_CLASSES, {
  variants: {
    variant: {
      // Primary CTA: Gold accent (Tuscan Sun) - use for main action per screen
      default:
        "bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active disabled:bg-secondary-disabled focus-visible:ring-ring",
      // Secondary action: Black/dark - use for form submits, secondary buttons
      neutral:
        "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active disabled:bg-primary-disabled focus-visible:ring-ring",
      // Danger action: Red - use for delete, destructive operations
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-error-hover active:bg-error-active disabled:bg-error-disabled focus-visible:ring-ring",
      // Tertiary: Border only - use for cancel, alternative actions
      outline:
        "border border-input bg-background hover:bg-surface-container-lowest active:bg-surface-container-low focus-visible:ring-ring",
      // Alias for default (backwards compatibility)
      secondary:
        "bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active disabled:bg-secondary-disabled focus-visible:ring-ring",
      // Minimal: No background - use for navigation, toolbars
      ghost:
        "hover:bg-surface-container-lowest active:bg-surface-container-low focus-visible:ring-ring",
      // Text link style
      link: "text-primary underline-offset-4 hover:underline hover:text-primary-hover focus-visible:ring-ring",
    },
    size: {
      default: "h-9 px-3.5 py-1.5",
      sm: "h-8 rounded-md px-3 text-xs",
      lg: "h-10 rounded-md px-4",
      pill: "h-10 rounded-full px-4 text-sm",
      "pill-lg": "h-11 rounded-full px-6 text-sm font-medium",
      icon: "h-9 w-9",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.memo(
  React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        className,
        variant,
        size,
        asChild = false,
        isLoading = false,
        loadingText,
        disabled,
        children,
        style: incomingStyle,
        ...props
      },
      ref,
    ) => {
      const Comp = asChild ? Slot : "button";
      const resolvedType =
        props.type ??
        (asChild
          ? undefined
          : ("button" as React.ButtonHTMLAttributes<HTMLButtonElement>["type"]));
      const isDisabled = disabled ?? isLoading;

      // When rendering as a Slot (asChild), we must pass exactly one child element.
      // Avoid injecting additional wrapper elements (e.g., loading spinner) to prevent Slot errors.
      if (asChild) {
        return (
          <Comp
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            aria-busy={isLoading ? "true" : undefined}
            aria-disabled={isDisabled ? "true" : undefined}
            disabled={isDisabled}
            style={incomingStyle}
            {...props}
          >
            {children}
          </Comp>
        );
      }

      return (
        <Comp
          className={cn(
            buttonVariants({ variant, size, className }),
            isLoading && "relative",
          )}
          ref={ref}
          aria-busy={isLoading ? "true" : undefined}
          aria-disabled={isDisabled ? "true" : undefined}
          disabled={isDisabled}
          style={incomingStyle}
          type={resolvedType}
          {...props}
        >
          {isLoading && (
            <span className="inline-flex items-center" aria-hidden>
              <LoadingSpinner size="sm" />
            </span>
          )}
          <span
            className={cn(
              "inline-flex items-center justify-center gap-2 [&>svg]:pointer-events-none [&>svg]:shrink-0 [&>svg]:mr-0",
              isLoading && "opacity-90",
            )}
          >
            {isLoading && loadingText ? loadingText : children}
          </span>
        </Comp>
      );
    },
  ),
);
Button.displayName = "Button";

export { Button, buttonVariants };
